/**
 * MiniHomeDesignsMatcher, slide-up popup variant of the home designs
 * library scoped to 3 quick questions and the top 3 matched floor
 * plans from the public `/api/home-designs/plans` catalog.
 *
 * Mirrors MiniHnlMatcher's visual treatment (anchored sheet on
 * desktop, bottom sheet on mobile) but scopes filters to floor-plan
 * design preferences (stories, bedrooms, size band).
 *
 * Used by the "Shortlist a floor plan" CTA in the Search Designs
 * mega menu. Anonymous users can still browse matches inline;
 * clicking a card deep-links to the plan detail page.
 *
 * Flow:
 *   questions (3 q's) → loading → results (top 3)
 *
 * Backend contract: GET /api/home-designs/plans?bedrooms=&stories=&min_sqm=&max_sqm=
 */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, MapPin, Building2, Bed, Bath, Car, Heart, Ruler, ChevronRight, Compass, Home as HomeIcon } from 'lucide-react';
import axios from 'axios';
import MatchDisclaimer from '../trust/MatchDisclaimer';
import MiniMatcherStateChip from './MiniMatcherStateChip';
import { SiriStyleSpinner } from '../../workspace/components/StandardLoadingScreen';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-02-19: State pill question added as Q1 across all mini matchers
// for parity. We don't filter the floor-plan dataset by state (designs
// are concepts, not state-bound) - capturing it here just ensures the
// downstream lead / shortlist save records the user's state correctly
// and gives the matcher the same first-question UX as the others.
const STATES = [
  { v: 'SA', l: 'South Australia' },
  { v: 'VIC', l: 'Victoria' },
  { v: 'NSW', l: 'New South Wales' },
  { v: 'QLD', l: 'Queensland' },
  { v: 'WA', l: 'Western Australia' },
  { v: 'TAS', l: 'Tasmania' },
  { v: 'ACT', l: 'ACT' },
  { v: 'NT', l: 'Northern Territory' },
];

const STORIES = [
  { v: 'Single', l: 'Single storey' },
  { v: 'Double', l: 'Double storey' },
  { v: 'ANY', l: 'Either' },
];

const BEDROOMS = [
  { v: 3, l: '3 bedrooms' },
  { v: 4, l: '4 bedrooms' },
  { v: 5, l: '5+ bedrooms' },
];

// Indicative size bands - we map these to min_sqm / max_sqm filters
// on the /plans endpoint. The "ANY" option skips the size filter.
const SIZES = [
  { v: 'compact', l: 'Compact · ≤ 200m²', min: 0, max: 200 },
  { v: 'medium', l: 'Medium · 200-280m²', min: 200, max: 280 },
  { v: 'large', l: 'Large · 280m²+', min: 280, max: 9999 },
  { v: 'ANY', l: 'Any size', min: null, max: null },
];

// Bathrooms - hard filter on the `bathrooms` column. We treat 2.5 the
// same as 2 (powder room edge case) so users don't fall off the cliff.
const BATHROOMS = [
  { v: 1, l: '1 bathroom' },
  { v: 2, l: '2 bathrooms' },
  { v: 3, l: '3+ bathrooms' },
  { v: 'ANY', l: 'Any' },
];

// Garage - string-contains match on the `garage` column ("Single Garage"
// / "Double Garage" / "Triple Garage"). "Any" means no filter.
const GARAGES = [
  { v: 'Single', l: 'Single garage' },
  { v: 'Double', l: 'Double garage' },
  { v: 'Triple', l: 'Triple garage' },
  { v: 'ANY', l: 'Any' },
];

// Soft "must-haves" - these don't filter the dataset (no column to
// match against) but boost score for plans whose plan_name or
// description hints at them. Multi-select, capped at 3.
const PRIORITIES = [
  { v: 'study',     l: 'Study / home office' },
  { v: 'theatre',   l: 'Home theatre / media room' },
  { v: 'alfresco',  l: 'Outdoor / alfresco' },
  { v: 'master',    l: 'Master away from kids' },
  { v: 'open_plan', l: 'Open-plan living' },
  { v: 'walk_in',   l: 'Walk-in pantry' },
];

function MiniHomeDesignsMatcher({ open, onClose, sourcePlacement = 'mega-menu-designs-bottom', sourcePage, initialState = null }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('questions');   // questions | loading | results
  const [state, setStateField] = useState(initialState || null);
  const [stories, setStories] = useState(null);
  const [bedrooms, setBedrooms] = useState(null);
  const [bathrooms, setBathrooms] = useState(null);
  const [size, setSize] = useState(null);
  const [garage, setGarage] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [qIdx, setQIdx] = useState(0);             // current question index 0..n
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  // Lock body scroll while the sheet is open (matches MiniHnlMatcher
  // behaviour so the page underneath doesn't scroll on iOS).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset state every time the popup re-opens so users always start
  // from a fresh quiz, not a stale answer set. Logged-in users with a
  // known state skip Q1 automatically; everyone else starts at Q1
  // (state pill grid) for parity with the other mini matchers.
  useEffect(() => {
    if (open) {
      setStep('questions');
      setStateField(initialState || null);
      setStories(null);
      setBedrooms(null);
      setBathrooms(null);
      setSize(null);
      setGarage(null);
      setPriorities([]);
      setQIdx(initialState ? 1 : 0);
      setResults([]);
      setError('');
    }
  }, [open, initialState]);

  const submit = async (overrides = {}) => {
    const _stories = overrides.stories ?? stories;
    const _bedrooms = overrides.bedrooms ?? bedrooms;
    const _bathrooms = overrides.bathrooms ?? bathrooms;
    const _size = overrides.size ?? size;
    const _garage = overrides.garage ?? garage;
    const _priorities = overrides.priorities ?? priorities;
    if (!(_stories && _bedrooms && _bathrooms && _size && _garage)) return;
    setStep('loading');
    setError('');
    try {
      // Build query - fetch a wide net (top 60) then score client-side
      // so we can mix soft signals (closeness to size band, bathroom
      // flex, priority keywords) without losing relevant designs.
      const params = { limit: 60 };
      if (_stories && _stories !== 'ANY') params.stories = _stories;
      if (_bedrooms) params.bedrooms = _bedrooms === 5 ? 5 : _bedrooms;
      const sizeDef = SIZES.find((s) => s.v === _size);
      if (sizeDef && sizeDef.min !== null) {
        params.min_sqm = sizeDef.min;
        params.max_sqm = sizeDef.max;
      }
      if (_garage && _garage !== 'ANY') params.garage = _garage;

      const minWait = new Promise((r) => setTimeout(r, 1400));
      const call = axios
        .get(`${API}/home-designs/plans`, { params })
        .then((r) => Array.isArray(r.data?.plans) ? r.data.plans : []);
      const [list] = await Promise.all([call, minWait]);

      // Progressive broadening: if the hard query came back empty,
      // drop the most restrictive filters one by one (garage → size →
      // bathrooms) so users always get SOMETHING relevant rather than
      // a blank wall.
      let plans = list;
      const broadenSteps = [
        () => ({ limit: 60, ...(params.stories ? { stories: params.stories } : {}), ...(params.bedrooms ? { bedrooms: params.bedrooms } : {}), ...(params.min_sqm ? { min_sqm: params.min_sqm, max_sqm: params.max_sqm } : {}) }),
        () => ({ limit: 60, ...(params.stories ? { stories: params.stories } : {}), ...(params.bedrooms ? { bedrooms: params.bedrooms } : {}) }),
        () => ({ limit: 60, ...(params.stories ? { stories: params.stories } : {}) }),
      ];
      for (const buildBroader of broadenSteps) {
        if (plans.length > 0) break;
        try {
          const r = await axios.get(`${API}/home-designs/plans`, { params: buildBroader() });
          plans = Array.isArray(r.data?.plans) ? r.data.plans : [];
        } catch { /* ignore */ }
      }

      const targetBeds = _bedrooms === 5 ? 5 : _bedrooms;
      // Combine plan_name + description into a single haystack for the
      // priorities soft-scoring keywords below.
      const priorityKeywords = {
        study:     ['study', 'office', 'home office', 'retreat'],
        theatre:   ['theatre', 'theater', 'media', 'cinema', 'rumpus'],
        alfresco:  ['alfresco', 'outdoor', 'patio', 'pergola', 'porch'],
        master:    ['master', 'parents retreat', 'main bed'],
        open_plan: ['open plan', 'open-plan', 'flow', 'living'],
        walk_in:   ['walk-in pantry', 'walk in pantry', 'butlers', "butler's"],
      };
      const scored = (plans || [])
        .map((p) => {
          let score = 50;
          // bedroom exact match boosts hardest - it's the strongest
          // user signal in design selection.
          if (p.bedrooms === targetBeds) score += 30;
          else if (p.bedrooms === targetBeds + 1 || p.bedrooms === targetBeds - 1) score += 12;
          // bathroom match (treat 2.5 as 2 → ceil for the comparison)
          if (_bathrooms && _bathrooms !== 'ANY') {
            const pBath = Math.ceil((p.bathrooms || 0));
            const target = _bathrooms === 3 ? 3 : _bathrooms;
            if (pBath === target || (target === 3 && pBath >= 3)) score += 14;
            else if (Math.abs(pBath - target) === 1) score += 6;
          } else {
            score += 4;
          }
          // storey match
          if (!_stories || _stories === 'ANY') score += 6;
          else if ((p.stories || '').toLowerCase() === _stories.toLowerCase()) score += 14;
          // size band match
          if (sizeDef && sizeDef.min !== null) {
            const s = p.total_sqm || 0;
            if (s >= sizeDef.min && s <= sizeDef.max) score += 10;
          } else {
            score += 4;
          }
          // garage match
          if (_garage && _garage !== 'ANY') {
            const g = (p.garage || '').toLowerCase();
            if (g.includes(_garage.toLowerCase())) score += 10;
          } else {
            score += 3;
          }
          // priorities soft scoring - search plan_name + description.
          const haystack = `${p.plan_name || ''} ${p.description || ''}`.toLowerCase();
          let pHits = 0;
          (_priorities || []).forEach((pri) => {
            const words = priorityKeywords[pri] || [];
            if (words.some((w) => haystack.includes(w))) {
              pHits += 1;
              score += 6;
            }
          });
          // image bonus so cards with facades surface above placeholder cards
          if (p.facade) score += 4;
          // small randomness so ties are stable but not boring
          score += Math.floor(Math.random() * 3);
          return {
            id: p.id,
            name: p.plan_name,
            builder: p.builder_name,
            beds: p.bedrooms,
            baths: p.bathrooms,
            sqm: p.total_sqm,
            stories: p.stories,
            cover: p.facade,
            href: `/home-designs/${p.builder_slug}/${p.plan_slug}`,
            match_score: Math.min(99, score),
            priority_hits: pHits,
          };
        })
        .sort((a, b) => b.match_score - a.match_score)
        .slice(0, 3);

      setResults(scored);
      setStep('results');
    } catch (err) {
      // Surface the real reason in the console so we can diagnose
      // recurring fetch failures without having to ship a debug build.
      console.warn('[MiniHomeDesignsMatcher] submit failed:', err);
      setError('We could not load designs right now. Browse all designs below, or try again in a moment.');
      // Move to the results step (which renders the empty-state card with
      // a Browse-all CTA) so users never end up stuck on the question
      // page with a bare error bar. Empty results trigger the
      // <mhd-empty> branch in the renderer, which gives them a way out.
      setResults([]);
      setStep('results');
    }
  };

  const goBrowseAll = () => {
    onClose?.();
    navigate('/home-designs');
  };

  if (!open) return null;

  const overlay = (
    <div
      className="bp-mm-overlay bp-mm-anchor-right"
      role="dialog"
      aria-modal="true"
      data-testid="mini-home-designs-matcher"
      data-source-placement={sourcePlacement}
      data-source-page={sourcePage || ''}
    >
      <button type="button" className="bp-mm-scrim" aria-label="Close" onClick={onClose} />
      <div className="bp-mm-panel">
        <header className="bp-mm-header bp-mm-intel-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '20px 22px 14px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#ffffff', margin: '0 0 6px', lineHeight: 1.25 }}>
              Find your floor plan
            </h2>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', margin: '0 0 8px', lineHeight: 1.45 }}>
              5 quick questions. We score 760+ designs against your brief.
            </p>
            <span className="bp-mm-intel-tagline" aria-hidden="true">
              <span className="bp-mm-intel-tagline-dot" />
              Powered by BuildPilot Intelligence
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="mini-home-designs-close" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.85)', padding: 4 }}>
            <X size={20} />
          </button>
        </header>

        <div className="bp-mm-body" style={{ padding: '0 22px 18px' }}>
          {/* 2026-02-19: Shared persistent state-chip - identical UX to
              MiniMatcher + MiniHnlMatcher. When state is empty the chip
              displays "We'll ask in a moment"; once the user picks a
              state via Q1 the chip flips to "Matching for: NSW". */}
          <MiniMatcherStateChip
            state={state || ''}
            onChange={(v) => {
              setStateField(v);
              if (qIdx === 0) setQIdx(1); // auto-advance past Q1 when state is set via chip
            }}
          />
          {step === 'questions' && (() => {
            const QUESTIONS = [
              { id: 'state', label: 'Which state are you building in?', icon: MapPin, options: STATES, cols: 2, set: setStateField, current: state },
              { id: 'stories', label: 'Single or double storey?', icon: Building2, options: STORIES, cols: 3, set: setStories, current: stories },
              { id: 'bedrooms', label: 'How many bedrooms?', icon: Bed, options: BEDROOMS, cols: 3, set: setBedrooms, current: bedrooms },
              { id: 'bathrooms', label: 'How many bathrooms?', icon: Bath, options: BATHROOMS, cols: 4, set: setBathrooms, current: bathrooms },
              { id: 'size', label: 'Home size', icon: Ruler, options: SIZES, cols: 2, set: setSize, current: size },
              { id: 'garage', label: 'Garage size', icon: Car, options: GARAGES, cols: 4, set: setGarage, current: garage },
              { id: 'priorities', label: 'Must-haves (pick up to 3)', icon: Heart, options: PRIORITIES, cols: 2, set: setPriorities, current: priorities, multi: true, optional: true },
            ];
            const q = QUESTIONS[Math.max(0, Math.min(qIdx, QUESTIONS.length - 1))];
            const Icon = q.icon;
            const isLast = qIdx >= QUESTIONS.length - 1;
            const pick = (v) => {
              if (q.multi) {
                // Multi-select toggling for soft priorities - capped at 3
                // so the AI doesn't try to score against 6 keyword groups.
                const cur = Array.isArray(q.current) ? q.current : [];
                const has = cur.includes(v);
                const next = has ? cur.filter((x) => x !== v) : [...cur, v].slice(0, 3);
                q.set(next);
                return; // require explicit "Continue" for multi-select
              }
              q.set(v);
              setTimeout(() => {
                if (isLast) {
                  submit({ [q.id]: v });
                } else {
                  setQIdx(qIdx + 1);
                }
              }, 220);
            };
            const isActive = (o) => {
              if (q.multi) return Array.isArray(q.current) && q.current.includes(o.v);
              return q.current === o.v;
            };
            return (
              <div className="bp-mm-questions-one-at-a-time">
              <div className="bp-mm-question-single">
                <div className="bp-mm-question-head">
                  <button
                    type="button"
                    onClick={() => qIdx > 0 && setQIdx(qIdx - 1)}
                    disabled={qIdx === 0}
                    aria-label="Back"
                    data-testid="mhd-back"
                    className="bp-mm-question-back"
                    style={qIdx === 0 ? { color: 'rgba(255,255,255,0.35)', cursor: 'default' } : undefined}
                  >‹ Back</button>
                  <span className="bp-mm-question-counter">
                    {qIdx + 1} of {QUESTIONS.length}
                  </span>
                </div>
                <label className="bp-mm-q-title-lg" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon size={14} /> {q.label}
                </label>
                <div className="bp-mm-pick-pills bp-mm-question-pills">
                  {q.options.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => pick(o.v)}
                      data-testid={`mhd-${q.id}-${String(o.v).toLowerCase()}`}
                      className={`bp-mm-pick-pill bp-mm-question-pill ${isActive(o) ? 'is-active' : ''}`}
                    >
                      <span className="bp-mm-pick-pill-label">{o.l}</span>
                    </button>
                  ))}
                </div>
                {q.multi && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button
                      type="button"
                      onClick={() => submit({})}
                      data-testid="mhd-priorities-skip"
                      className="bp-mm-question-back"
                      style={{ flex: 1, padding: '12px 0', textAlign: 'center' }}
                    >Skip</button>
                    <button
                      type="button"
                      onClick={() => submit({})}
                      data-testid="mhd-priorities-continue"
                      className="bp-mm-primary"
                      style={{ flex: 2 }}
                    >Find my designs <ChevronRight size={16} /></button>
                  </div>
                )}
                {error && <p className="bp-mm-error">{error}</p>}
              </div>
              </div>
            );
          })()}

          {step === 'loading' && (
            <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <SiriStyleSpinner size={70} />
              <p style={{ fontSize: 13, color: '#ffffff', margin: '16px 0 0', fontWeight: 500 }}>
                Scoring 760+ floor plans…
              </p>
              <p className="bp-mm-intel-tagline" style={{ marginTop: 8, justifyContent: 'center' }}>
                <span className="bp-mm-intel-tagline-dot" />
                BuildPilot Intelligence is matching you
              </p>
            </div>
          )}

          {step === 'results' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {results.length === 0 ? (
                <div className="bp-mm-concierge-soft" data-testid="mhd-empty">
                  {error && <p className="bp-mm-error" data-testid="mhd-empty-error">{error}</p>}
                  <p className="bp-mm-concierge-soft-msg">
                    {error
                      ? 'We hit a snag scoring your brief. Browse the full design catalogue below, or close this and try again in a moment.'
                      : 'No floor plans match your brief right now. Browse the full design catalogue or relax a question to see more options.'}
                  </p>
                  <button
                    type="button"
                    className="bp-mm-primary"
                    onClick={goBrowseAll}
                    data-testid="mhd-browse-all-empty"
                  >
                    Browse all 760+ designs
                    <ChevronRight size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0 }}>
                    Top {results.length} matches for you
                  </p>
                  {results.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => { onClose?.(); navigate(r.href); }}
                      data-testid={`mhd-result-${r.id}`}
                      className="bp-mm-intel-result-card"
                      style={{
                        display: 'flex',
                        gap: 12,
                        padding: 12,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 14,
                        textDecoration: 'none',
                        color: '#1d1d1f',
                        textAlign: 'left',
                        cursor: 'pointer',
                        width: '100%',
                      }}
                    >
                      <div style={{ width: 72, height: 72, borderRadius: 10, overflow: 'hidden', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {r.cover ? (
                          <img src={r.cover} alt={r.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                        ) : (
                          <HomeIcon size={26} color="#86868b" />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline' }}>
                          <p style={{ fontSize: 14, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.name}
                          </p>
                          <span className="bp-mm-intel-match-chip" style={{ flexShrink: 0 }}>
                            {r.match_score}% match
                          </span>
                        </div>
                        <p style={{ fontSize: 12, color: '#86868b', margin: '2px 0 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.builder}
                        </p>
                        <p style={{ fontSize: 12, color: '#1d1d1f', margin: 0, fontWeight: 500 }}>
                          {[
                            r.beds ? `${r.beds} bed` : null,
                            r.baths ? `${r.baths} bath` : null,
                            r.sqm ? `${Math.round(r.sqm)}m²` : null,
                            r.stories,
                          ].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={goBrowseAll}
                    data-testid="mhd-browse-all"
                    style={{
                      marginTop: 6,
                      padding: '10px 16px',
                      background: 'rgba(255,255,255,0.10)',
                      color: '#ffffff',
                      border: '1px solid rgba(255,255,255,0.45)',
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      backdropFilter: 'blur(6px)',
                    }}
                  >
                    Browse all 760+ designs
                    <ChevronRight size={14} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <footer className="bp-mm-foot">
          <MatchDisclaimer inline className="!text-[10.5px] mb-1" />
          <a href="/one-on-one-support" className="bp-mm-foot-link">
            Or talk to BuildPilot 1:1 →
          </a>
        </footer>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}

export default MiniHomeDesignsMatcher;
